import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import queryString from 'query-string';
import settings from '../../config/settings';
import Footer from '../Footer/Footer';
import { Application, getApplication} from '../../api/application';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import { Spinner } from '@danfoss/webex-ui';
import { getUserLanguage } from '../../utils/userLanguage';
import { resetMultifactorInfo } from '../../api/multifactor';
import { useSharedStyles } from '../../sharedStyles/Styles';
import { pushEvent } from '../../Services/AnalyticsService';
import LogoAndBackground from '../Header/LogoAndBackground';
import ConfirmationContent from '../ConfirmationContent';
import { useAuthentication } from '../../Providers/AuthenticationProvider';
import DOMPurify from 'dompurify';
import { AppState } from '../../store';

const MFAResetConfirmation: React.FC<Props> = ({ profile, cultures }) => {
    const { t } = useTranslation();
    const classes = useSharedStyles();
    const { getTokenSilently } = useAuthentication();

    const [application, setApplication] = useState({} as Application);
    const [isLoading, setIsLoading] = useState(true);
    
    useEffect(() => {
        setIsLoading(true);

        //Get querystring parameters
        const parsed = queryString.parse(window.location.search);

        const getApp = async () => {

            // take clientId if part of querystring otherwise default to My Profile clientId
            // @ts-ignore
            const cId = DOMPurify.sanitize(parsed.clientId) || settings.auth0.clientId;

            const lan = getUserLanguage(profile, cultures.cultures);
            
            // Get application based on clientId in URL
            let app = await getApplication(cId as string, lan);

            // If the application hasn't got a url, then default back to the default 
            // application "Danfoss Profile"
            if (!app.url) {
                app = await getApplication(settings.auth0.clientId, cultures.currentCulture);
            }
        
            setApplication(app);  
            
            const token = await getTokenSilently();

            await resetMultifactorInfo(profile?.sub || '', token)
            
            setIsLoading(false);
        }

        getApp();


        // Push an event that a user entered the mfa reset page
        pushEvent(
            {
                'event': 'virtualPageview',
                'page': `${(window as any).location.hostname}/mfareset`
            }
		);
        
    }, []);
    
    return isLoading ? (<Spinner visible={isLoading} />) : (
        <React.Fragment>
            <LogoAndBackground classes={classes} />
            <ConfirmationContent
                classes={classes}
                message={'mfa reset confirmation'}
                application={application}
                iconURL={settings.staticFiles.endpoint + '/images/system/ErrorOptionChecked.svg'}
                mfaResetHeader={t('privacy_and_security.mfa_settings.reset_mfa_confirmation_header')}
                mfaResetBody={parse(t('privacy_and_security.mfa_settings.reset_mfa_confirmation_body'))}
                mfaResetButton={parse(t('privacy_and_security.mfa_settings.reset_mfa_confirmation_button', { application: (application.translations?.name || application.name || '') }))}
            />
            <Footer contentClass={classes.footerContent} />
        </React.Fragment>
    );
}

const mapStateToProps = (state: AppState) => ({
    cultures: state.cultures,
    profile: state.profile
});

type Props = ReturnType<typeof mapStateToProps>;

export default connect(mapStateToProps)(MFAResetConfirmation);
